import React, { useState, useEffect } from 'react';
import './ImageImportModal.css';

interface Props {
  open: boolean;
  onClose: () => void;
  onExport: (opts: { scale: number; includeNumbers: boolean; cellSize: number; mode?: 'single' | 'pdf'; hSlices?: number; vSlices?: number; includeBillOfMaterials?: boolean; includeColorIndexInCells?: boolean }) => void;
  cellSize: number;
  totalWidthCells: number;
  totalHeightCells: number;
}

export default function ExportModal(props: Props) {
  const { open, onClose, onExport, cellSize } = props;
  const isDarkMode = typeof document !== 'undefined' && document.documentElement.classList.contains('dark');
  const [scale, setScale] = useState(3);
  const [isResizing, setIsResizing] = useState(false);
  const [includeNumbers, setIncludeNumbers] = useState(true);
  const [includeBOM, setIncludeBOM] = useState(false);
  const [includeColorIndexInCells, setIncludeColorIndexInCells] = useState(false);
  const [mode, setMode] = useState<'single' | 'pdf'>('single');
  // default slices compute: chunk size 50x50
  const chunk = 50;
  const defaultHSlices = Math.max(1, Math.ceil((props.totalWidthCells || 1) / chunk));
  const defaultVSlices = Math.max(1, Math.ceil((props.totalHeightCells || 1) / chunk));
  const [hSlices, setHSlices] = useState<number>(defaultHSlices);
  const [vSlices, setVSlices] = useState<number>(defaultVSlices);

  // When the modal opens, ensure the slice inputs are initialized to the calculated defaults
  useEffect(() => {
    if (open) {
      setHSlices(defaultHSlices);
      setVSlices(defaultVSlices);
    }
  }, [open, defaultHSlices, defaultVSlices]);

  // trigger a brief modal transform when mode changes to smooth perceived size change
  useEffect(() => {
    setIsResizing(true);
    const id = window.setTimeout(() => setIsResizing(false), 260);
    return () => window.clearTimeout(id);
  }, [mode]);

  // draw preview minimap showing slice boundaries; use a small canvas
  useEffect(() => {
    const cv = document.getElementById('export-preview-canvas') as HTMLCanvasElement | null;
    if (!cv) return;
    // size preview to match full-grid aspect ratio (small but visible)
    const aspect = (props.totalHeightCells || 1) / Math.max(1, (props.totalWidthCells || 1));
    const previewMaxW = 240;
    const previewW = previewMaxW;
    const previewH = Math.max(36, Math.min(320, Math.round(previewW * aspect)));
    // set canvas pixel dimensions and style (center horizontally)
    cv.width = previewW; cv.height = previewH;
    cv.style.width = `${previewW}px`;
    cv.style.height = `${previewH}px`;
    cv.style.display = 'block';
    cv.style.margin = '0 auto';
    const ctx = cv.getContext('2d'); if (!ctx) return;
    const w = cv.width; const h = cv.height;
    ctx.clearRect(0, 0, w, h);
    // detect dark mode and pick appropriate colors
    const isDark = typeof document !== 'undefined' && document.documentElement.classList.contains('dark');
    const bg = isDark ? '#222' : '#fafafa';
    const border = isDark ? 'rgba(255,255,255,0.06)' : '#ddd';
    const sliceLine = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.12)';
    const cellLine = isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.06)';
    // apply background and border
    ctx.fillStyle = bg; ctx.fillRect(0, 0, w, h);
    cv.style.border = `1px solid ${border}`;
    // compute inner area with padding
    const pad = 6; const innerW = w - pad * 2; const innerH = h - pad * 2;
    ctx.strokeStyle = border; ctx.lineWidth = 1; ctx.strokeRect(pad, pad, innerW, innerH);
    // draw slice gridlines (slightly thicker for visibility)
    ctx.strokeStyle = sliceLine; ctx.lineWidth = 1.5;
    for (let i = 1; i < hSlices; i++) { const gx = pad + Math.round((i / hSlices) * innerW); ctx.beginPath(); ctx.moveTo(gx + 0.5, pad); ctx.lineTo(gx + 0.5, pad + innerH); ctx.stroke(); }
    for (let j = 1; j < vSlices; j++) { const gy = pad + Math.round((j / vSlices) * innerH); ctx.beginPath(); ctx.moveTo(pad, gy + 0.5); ctx.lineTo(pad + innerW, gy + 0.5); ctx.stroke(); }
    // optionally draw coarse cell grid if small
    const maxCellsToDraw = 60;
    if (props.totalWidthCells <= maxCellsToDraw && props.totalHeightCells <= maxCellsToDraw) {
      ctx.strokeStyle = cellLine; ctx.lineWidth = 0.5;
      for (let cx = 1; cx < props.totalWidthCells; cx++) { const x = pad + Math.round((cx / props.totalWidthCells) * innerW); ctx.beginPath(); ctx.moveTo(x + 0.25, pad); ctx.lineTo(x + 0.25, pad + innerH); ctx.stroke(); }
      for (let ry = 1; ry < props.totalHeightCells; ry++) { const y = pad + Math.round((ry / props.totalHeightCells) * innerH); ctx.beginPath(); ctx.moveTo(pad, y + 0.25); ctx.lineTo(pad + innerW, y + 0.25); ctx.stroke(); }
    }
    // no highlighted tile in preview — show only slice gridlines and (optionally) coarse cell grid
  }, [hSlices, vSlices, props.totalWidthCells, props.totalHeightCells, open, defaultHSlices, defaultVSlices, mode]);

  if (!open) return null;

  return (
    <div className="imodal-backdrop" onClick={onClose}>
      <div
        className="imodal-panel"
        style={{
          width: 420,
          transition: 'transform 220ms cubic-bezier(.2,.9,.2,1)',
          transform: isResizing ? 'scale(1.01)' : 'scale(1)'
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h3 style={{ margin: 0 }}>Export Image</h3>
          <button onClick={onClose} style={{ border: 'none', background: 'transparent', fontSize: 18 }}>✕</button>
        </div>
        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div className="imodal-controls" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ display: 'flex', gap: 0, padding: 2, borderRadius: 10, background: 'transparent', border: isDarkMode ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)' }}>
                <button
                  type="button"
                  onClick={() => setMode('single')}
                  aria-pressed={mode === 'single'}
                  style={{
                    padding: '8px 14px',
                    border: 'none',
                    borderRadius: '8px 0 0 8px',
                    background: mode === 'single' ? '#2196f3' : (isDarkMode ? 'rgba(255,255,255,0.03)' : 'transparent'),
                    color: mode === 'single' ? '#fff' : (isDarkMode ? '#eee' : undefined),
                    cursor: 'pointer',
                    minWidth: 120,
                    outline: 'none'
                  }}
                >
                  Single image
                </button>
                <button
                  type="button"
                  onClick={() => setMode('pdf')}
                  aria-pressed={mode === 'pdf'}
                  style={{
                    padding: '8px 14px',
                    border: 'none',
                    borderRadius: '0 8px 8px 0',
                    background: mode === 'pdf' ? '#2196f3' : (isDarkMode ? 'rgba(255,255,255,0.03)' : 'transparent'),
                    color: mode === 'pdf' ? '#fff' : (isDarkMode ? '#eee' : undefined),
                    cursor: 'pointer',
                    minWidth: 120,
                    outline: 'none'
                  }}
                >
                  Multi-page PDF
                </button>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              {mode === 'single' && (
                // <label style={{ display: 'flex', flexDirection: 'column' }}>
                //   Scale (pixel multiplier)
                //   <select value={String(scale)} onChange={e => setScale(parseInt(e.target.value || '3'))} style={{ marginTop: 6 }}>
                //     <option value="1">1×</option>
                //     <option value="2">2×</option>
                //     <option value="3">3×</option>
                //     <option value="4">4×</option>
                //   </select>
                // </label>
                <div className="control" style={{width:'100%',alignItems:'center'}}>
                  <label className="control-label" style={{textAlign:'center'}}>Scale (pixel multiplier) <span style={{marginLeft:8,fontWeight:600}}>{scale}×</span></label>
                  <input className="control-input range" type="range" min={1} max={5} step={1} value={scale} onChange={e=>setScale(parseFloat(e.target.value||'3'))} />
                </div>
              )}

            </div>



            {mode === 'pdf' && (
              <>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center', justifyContent: 'center' }}>
                  <label style={{ display: 'flex', flexDirection: 'column' }}>
                    Horizontal slices
                    <input type="number" min={1} value={hSlices} onChange={e => setHSlices(Math.max(1, parseInt(e.target.value || '1')))} style={{ width: 80, marginTop: 6 }} />
                  </label>
                  <label style={{ display: 'flex', flexDirection: 'column' }}>
                    Vertical slices
                    <input type="number" min={1} value={vSlices} onChange={e => setVSlices(Math.max(1, parseInt(e.target.value || '1')))} style={{ width: 80, marginTop: 6 }} />
                  </label>
                </div>
                <div style={{ marginLeft: 8, fontSize: 12, color: '#666' }}>Defaults create ~50×50 cell tiles</div>
                {/* Preview minimap: visualize how slices divide the full grid */}
                <div style={{ display: 'flex', justifyContent: 'center', gap: 12 }}>
                  <div style={{ flex: '0 0 auto' }}>
                    <canvas id="export-preview-canvas" width={200} height={120} style={{ border: '1px solid rgba(0,0,0,0.08)', background: '#fff' }} />
                  </div>
                </div>
              </>
            )}

          </div>
          <label style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <input type="checkbox" checked={includeNumbers} onChange={e => setIncludeNumbers(e.target.checked)} style={{ marginTop: 0 }} />
            <span>Include numbers</span>
          </label>
          {mode === 'pdf' && (
            <div style={{marginTop:8,borderTop: isDarkMode ? '1px solid rgba(255,255,255,0.04)' : '1px solid rgba(0,0,0,0.06)', paddingTop:10}}>
              <div style={{fontWeight:700,marginBottom:6}}>Advanced</div>
              <label style={{display:'flex',flexDirection:'row',alignItems:'center',gap:8}}>
                <input type="checkbox" checked={includeBOM} onChange={e=>setIncludeBOM(e.target.checked)} style={{marginTop:0}} />
                <div>
                  <div>Include bill-of-materials</div>
                  <div style={{fontSize:12,color:isDarkMode ? '#ccc' : '#666'}}>Add a page at the start of the PDF listing all colors and their cell counts.</div>
                </div>
              </label>
              <label style={{display:'flex',flexDirection:'row',alignItems:'center',gap:8,marginTop:8}}>
                <input type="checkbox" checked={includeColorIndexInCells} onChange={e=>setIncludeColorIndexInCells(e.target.checked)} style={{marginTop:0}} />
                <div>
                  <div>Show color ID in each cell</div>
                  <div style={{fontSize:12,color:isDarkMode ? '#ccc' : '#666'}}>Write the color index number small inside each exported cell.</div>
                </div>
              </label>
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 8 }}>
            <button onClick={() => { onExport({ scale, includeNumbers, cellSize, mode, hSlices, vSlices, includeBillOfMaterials: includeBOM, includeColorIndexInCells: includeColorIndexInCells }); onClose(); }} style={{ background: '#2196f3', color: '#fff', border: 'none', padding: '8px 12px', borderRadius: 6 }}>{mode === 'pdf' ? 'Export PDF' : 'Export Image'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
